/**
 * A weight: the thing the whole bench is about lifting.
 *
 * One anchorage, its hook, on the top face — which is where a rope meets it
 * and where the joining dot is, because they are the same point. Drawn to its
 * mass, so a bench of weights is readable before any label is.
 */
import { JOINS, numbered, PartKind } from '../registry';
import { ACROSS, DOWN } from '../vec';
import { SHEAVE_R, SHEAVE_R_ON_FACE } from './sheave';
import { Part, Port, RopeContact, Vec2, World } from '../types';
import { frameOf } from '../world';

export interface WeightData {
    mass: number;
    /** How it grips the floor — see `PartKind.floorGrip`. Absent is smooth. */
    floorGrip?: number | 'fixed';
}

/**
 * How wide a block is, whatever it weighs — half of it.
 *
 * Written against the WHEEL rather than as a number, because the relationship
 * is the point: a block and the sheave it hangs from are the two things a
 * reader compares sizes of without meaning to, so if the wheel is ever resized
 * the block follows.
 *
 * NARROWER than the wheel, and that is the whole reason for the fraction. A
 * rope leaves a wheel one radius out from its centre, so a block hanging on
 * that rope is centred one radius clear of whatever the wheel is bolted to —
 * and at exactly a radius wide its edge lands ON that thing. A pulley at the
 * corner of a table gave a hanging block a face flush against the table's
 * vertical wall, touching it down its whole length, which reads as resting on
 * it rather than hanging beside it. The quarter it gives up is the gap.
 *
 * Fixed across every mass. More mass makes a block TALLER rather than fatter,
 * which is what more mass does to a bar of the same stuff at the same width:
 * the height is proportional to it.
 */
export const WEIGHT_HALF_W = SHEAVE_R * 0.75;

/**
 * How tall a block may get, at each end — half of it.
 *
 * Both ends matter and for different reasons. Too short and a block is a line
 * rather than a thing that could hold a kilogram; too tall and it is a column
 * that reaches the floor before the machine has done anything. Between them
 * the height is straight in the mass, because the width is fixed and a block
 * of one material at one width is as tall as it is heavy.
 *
 * Set so that a few kilograms is roughly square and the heaviest is about
 * twice as tall as it is wide, which is the range a reader can judge by eye.
 */
export const WEIGHT_HALF_MIN = 0.12;
export const WEIGHT_HALF_MAX = 0.45;

/**
 * How tall a block STANDING ON A FACE is — half of it: a pulley's radius.
 *
 * So that a rope from it over a pulley fastened on the same surface runs
 * PARALLEL to the face, which is how every textbook draws a block on a ramp
 * or a table hauled over an edge. A rope leaves a block at its centre, half
 * its height above the face; it arrives on a wheel at the top of its rim, one
 * radius above the wheel's centre; and with the wheel's centre on the face —
 * a bolt closed to nothing — the two heights are equal only when the block is
 * as tall as the wheel is wide.
 *
 * Fixed rather than grown with mass, because a rope cannot be parallel to two
 * faces for two blocks of two heights over one wheel. Mass is still in the
 * block's colour and its label; a HANGING block still grows with it.
 */
export const WEIGHT_ON_FACE_HALF = SHEAVE_R_ON_FACE;

export const weightHalf = (m: number): number =>
    Math.max(WEIGHT_HALF_MIN, Math.min(WEIGHT_HALF_MAX, 0.12 + 0.018 * Math.max(0, m)));

export const weight: PartKind<WeightData> = {
    key: 'weight',
    label: 'Mass block',
    home: 2.6,
    create: (at, id) => ({ id, kind: 'weight', at, data: { mass: 2 } }),
    /**
     * Its hook is its CENTRE — a point mass.
     *
     * The block is still DRAWN at a size that grows with its mass, because a
     * heavy thing looking heavier is worth having. But nothing in the
     * arithmetic knows about that size: a rope ties to the point, and the
     * weight acts at the point, so there is no offset to forget and no second
     * place the force could be said to act. Sizes come back once the
     * point-mass bench is right.
     */
    /*
     * And a point on each SIDE, and one underneath — so one block can be tied
     * to several things at once, each where it would really be tied: hauled
     * left and right across a table, or with a second load hung below it. A
     * rope could always be tied to the one hook more than once, but a single
     * dot gave no way to see that, or to say which side a rope pulled from.
     *
     * In the block's own frame, so on a slope its sides are its two ends ALONG
     * the slope, at the height of its centre — which is what keeps a rope from
     * either of them over a pulley on that surface parallel to the face, the
     * same as from the hook. No point underneath a block standing on a face:
     * that is where it is pressed against the surface.
     */
    ports: (part, w): Port[] => {
        const f = w ? frameOf(w, part as unknown as Part) : ACROSS;
        // A quarter turn clockwise from the frame: down, with y running up.
        const down = { x: f.y, y: -f.x };
        const hh = part.support ? WEIGHT_ON_FACE_HALF : weightHalf(part.data.mass);
        const along = (s: number, d: number): Vec2 => ({
            x: part.at.x + f.x * s + down.x * d,
            y: part.at.y + f.y * s + down.y * d,
        });
        const point = (id: string, at: Vec2): Port => ({ id, partId: part.id, role: 'anchorage', joins: JOINS.one, at });
        return [
            // The hook takes several — a block hung from two ropes, or held
            // from above and pulled from below; each side takes one.
            { ...point('hook', { ...part.at }), joins: JOINS.any },
            point('left', along(-WEIGHT_HALF_W, 0)),
            point('right', along(WEIGHT_HALF_W, 0)),
            ...(part.support ? [] : [point('bottom', along(0, hh))]),
        ];
    },
    /** The centre hook needs no suffix — it is what "tied to m₁" has always
     *  meant. The others say which side: m₁_L, m₁_R, m₁_B. */
    portName: (port) => ({ left: 'L', right: 'R', bottom: 'B' } as Record<string, string>)[port.id] ?? null,
    mass: (part) => part.data.mass,
    floorGrip: (part) => part.data.floorGrip ?? 0,
    extent: (part) => ({ x: WEIGHT_HALF_W, y: part.support ? WEIGHT_ON_FACE_HALF : weightHalf(part.data.mass) }),
    /* TWO freedoms, because a body moves in a PLANE.
     *
     * One vertical axis was the model saying a weight may only go up and down,
     * and it is why a sideways force on one had nowhere to go: the dynamics
     * dropped the component it had no coordinate for, and a run could leave
     * newtons on a part while reporting no residual at all. The setup solve
     * has used both coordinates since it was rebuilt; this is the dynamics
     * catching up, so the two differ in ΣF = 0 versus ΣF = ma and in nothing
     * else. A genuine restriction — a runner's beam, a block's face — is still
     * one axis, because those are real. */
    freedom: () => ({ axes: [ACROSS, DOWN] }),
    bondable: true,
    keyFacts: (part) => [`${+part.data.mass.toFixed(2)} kg`],
    ropeContact: (part, port): RopeContact => ({ at: port.at }),
    /* LOWER CASE. A mass is m₁, m₂, m₃ in every book a reader will open, and
     * the bench was calling them M₁, M₂ — which a student reads as a different
     * quantity from the m in `mg`, sitting right beside it on the same
     * diagram. */
    tag: 'm',
    /** Inside the block, always — a weight is the one part wide enough to
     *  carry its own name, and a name on the thing it names never has to be
     *  traced back to it. Its glyph draws it. */
    nameAt: () => null,
    name: (part, w: World) => numbered(part, w, 'm'),
};
